/**
 * Shared camera classification for salon face vs USB microscope capture.
 * Custom assembled Android boards often confuse built-in and UVC cameras.
 */

import { isNyufazSalonKioskViewport } from './kioskViewport';

export const MACRO_USB_DEVICE_KEY = 'nyafaz_macro_usb_device_id';
export const MACRO_NATIVE_USB_ID_KEY = 'nyafaz_macro_native_usb_id';
export const SALON_FACE_CAMERA_DEVICE_KEY = 'nyafaz_salon_face_camera_device_id';

// Do NOT match bare "camera2 N" (Android WebView labels every Camera2 device that way).
export const EXTERNAL_RE =
  /\b(usb|uvc|otg|external|webcam|wired|hdmi|capture|microscope|document|usb\s*mic|xcx)\b/i;
export const FRONT_RE = /\b(front|user|facing\s*front|selfie|facetime)\b/i;
export const REAR_RE = /\b(back|rear|environment|facing\s*back|world)\b/i;
/** USB microscope / dermatoscope — never use for face scan. */
export const MICRO_UVC_RE =
  /\b(usb\s*mic|xcx|microscope|dermatoscope|dermascope|macro|scalp|otg\s*cam)\b/i;
/** Generic USB HD / conference cams used as kiosk face camera on custom boards. */
export const FACE_USB_HD_RE = /\b(hd\s*camera|hd\s*webcam|webcam|emeet|logitech|c920|c930|brio)\b/i;

/**
 * Parse Android WebView ExtCam labels like "camera2 1, facing front".
 * @param {string} label
 * @returns {number|null}
 */
export function parseCamera2Index(label) {
  const m = String(label || '').match(/camera2\s+(\d+)/i);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {string} label
 * @param {number} index
 * @param {number} total
 * @returns {'builtin-front'|'builtin-rear'|'external'|'unknown'}
 */
export function classifyVideoInput(label, index, total) {
  const text = String(label || '').trim();
  if (MICRO_UVC_RE.test(text) || EXTERNAL_RE.test(text)) return 'external';
  // Rockchip ExtCam: "camera2 N, facing front" is USB, not a true built-in selfie cam.
  // Leave as unknown so pickBuiltinFrontCamera / pickUsbMicroscopeCamera can choose by id.
  if (parseCamera2Index(text) != null) return 'unknown';
  if (FRONT_RE.test(text)) return 'builtin-front';
  if (REAR_RE.test(text)) return 'builtin-rear';

  // Custom Android WebView: labels often blank.
  // Typical order: 0=front, 1=rear, last=USB microscope when 3+ cams.
  if (!text && total >= 3 && index === total - 1) return 'external';
  if (!text && index === 0) return 'builtin-front';
  if (!text && index === 1 && total >= 2) return 'builtin-rear';
  return 'unknown';
}

/**
 * @param {MediaDeviceInfo} device
 * @param {number} index
 * @param {number} total
 */
export function cameraDisplayName(device, index, total) {
  const kind = classifyVideoInput(device?.label, index, total);
  const raw = String(device?.label || '').trim();
  if (kind === 'external') return raw ? `USB microscope (${raw})` : `USB microscope (Camera ${index + 1})`;
  if (kind === 'builtin-front') return raw ? `Main front camera (${raw})` : 'Main front camera';
  if (kind === 'builtin-rear') return raw ? `Main rear camera (${raw})` : 'Main rear camera';
  return raw || `Camera ${index + 1}`;
}

/**
 * @param {MediaDeviceInfo[]} cams
 * @returns {Array<{ device: MediaDeviceInfo, kind: string, displayName: string, index: number }>}
 */
export function describeVideoInputs(cams) {
  const list = Array.isArray(cams) ? cams.filter((d) => d && d.deviceId) : [];
  return list.map((device, index) => {
    const kind = classifyVideoInput(device.label, index, list.length);
    return {
      device,
      kind,
      index,
      displayName: cameraDisplayName(device, index, list.length),
    };
  });
}

/**
 * True built-in / SoC camera (e.g. Rockchip camera2 100 INTERNAL), not USB ExtCam.
 * @param {MediaDeviceInfo|null|undefined} device
 */
export function isBuiltinDeviceCamera(device) {
  const label = String(device?.label || '');
  if (!label.trim()) return false;
  if (MICRO_UVC_RE.test(label) || FACE_USB_HD_RE.test(label)) return false;
  if (/\binternal\b/i.test(label)) return true;
  const n = parseCamera2Index(label);
  // ExtCam USB nodes are usually 0/1; INTERNAL back is often 100+.
  if (n != null && n >= 100) return true;
  if (n == null && FRONT_RE.test(label) && !EXTERNAL_RE.test(label)) return true;
  return false;
}

/**
 * USB microscope / dermatoscope candidates that must never be used for face scan.
 * On GM76S dual ExtCam: lowest camera2 + "facing back" ≈ USB MIC.
 * @param {MediaDeviceInfo|null|undefined} device
 * @param {MediaDeviceInfo[]} [cams]
 */
export function isUsbMicroscopeCandidate(device, cams = []) {
  if (!device) return false;
  const label = String(device.label || '');
  const id = String(device.deviceId || '').trim();
  if (!id && !label) return false;

  const storedMacro = readStoredMacroUsbDeviceId();
  if (storedMacro && id && storedMacro === id) return true;
  if (MICRO_UVC_RE.test(label)) return true;
  if (FACE_USB_HD_RE.test(label) || isBuiltinDeviceCamera(device)) return false;

  const list = Array.isArray(cams) ? cams.filter((d) => d && d.deviceId) : [];
  const cam2 = list
    .map((d) => ({ d, n: parseCamera2Index(d.label || '') }))
    .filter((x) => x.n != null)
    .sort((a, b) => a.n - b.n);
  const n = parseCamera2Index(label);
  if (cam2.length >= 2 && n != null) {
    const hasFront = cam2.some((x) => FRONT_RE.test(x.d.label || ''));
    const hasBack = cam2.some((x) => REAR_RE.test(x.d.label || ''));
    // Dual ExtCam: facing-back / lowest id is the macro UVC, not the face cam.
    if (hasFront && hasBack && REAR_RE.test(label)) return true;
    if (hasFront && n === cam2[0].n && !FRONT_RE.test(label)) return true;
  }
  return false;
}

/**
 * Built-in / kiosk face camera — never the USB microscope (USB MIC / XCX).
 * Preference on custom boards (GM76S):
 * 1) SoC/internal device camera (camera2 100+)
 * 2) USB HD face cam / facing-front ExtCam
 * 3) Highest non-microscope camera2
 * Never returns the USB MIC / facing-back macro node.
 * @param {MediaDeviceInfo[]} cams
 * @returns {MediaDeviceInfo|null}
 */
export function pickBuiltinFrontCamera(cams) {
  const list = Array.isArray(cams) ? cams.filter((d) => d && d.deviceId) : [];
  if (!list.length) return null;

  const facePool = list.filter((d) => !isUsbMicroscopeCandidate(d, list));
  const pool = facePool.length ? facePool : list;

  const builtin = pool.find((d) => isBuiltinDeviceCamera(d));
  if (builtin) return builtin;

  const hd = pool.find((d) => FACE_USB_HD_RE.test(d.label || ''));
  if (hd) return hd;

  const cam2 = pool
    .map((d) => ({ d, n: parseCamera2Index(d.label || '') }))
    .filter((x) => x.n != null)
    .sort((a, b) => a.n - b.n);
  if (cam2.length >= 1) {
    const frontLabeled = [...cam2]
      .reverse()
      .find((x) => FRONT_RE.test(x.d.label || '') && !isUsbMicroscopeCandidate(x.d, list));
    if (frontLabeled) return frontLabeled.d;
    // Prefer highest non-microscope ExtCam / internal id.
    const nonMicroCam2 = [...cam2].reverse().find((x) => !isUsbMicroscopeCandidate(x.d, list));
    if (nonMicroCam2) return nonMicroCam2.d;
  }

  const described = describeVideoInputs(pool);
  const front = described.find((c) => c.kind === 'builtin-front');
  if (front) return front.device;
  const nonUsb = described.find((c) => c.kind !== 'external');
  if (nonUsb) return nonUsb.device;
  return pool[0];
}

/**
 * Built-in rear / world camera for kiosk-assisted capture (staff holds device).
 * Never returns USB microscope / macro nodes.
 * @param {MediaDeviceInfo[]} cams
 * @returns {MediaDeviceInfo|null}
 */
export function pickBuiltinRearCamera(cams) {
  const list = Array.isArray(cams) ? cams.filter((d) => d && d.deviceId) : [];
  if (!list.length) return null;

  const facePool = list.filter((d) => !isUsbMicroscopeCandidate(d, list));
  const pool = facePool.length ? facePool : list;

  const described = describeVideoInputs(pool);
  const rearKind = described.find((c) => c.kind === 'builtin-rear');
  if (rearKind) return rearKind.device;

  const cam2 = pool
    .map((d) => ({ d, n: parseCamera2Index(d.label || ''), label: d.label || '' }))
    .filter((x) => x.n != null)
    .sort((a, b) => a.n - b.n);
  const rearLabeled = cam2.find(
    (x) => REAR_RE.test(x.label) && !isUsbMicroscopeCandidate(x.d, list),
  );
  if (rearLabeled) return rearLabeled.d;

  if (pool.length >= 2) {
    const second = described[1];
    if (second && second.kind === 'builtin-rear') return second.device;
    const candidate = pool[1];
    if (candidate && !isUsbMicroscopeCandidate(candidate, list)) return candidate;
  }

  const nonFront = described.filter((c) => c.kind !== 'builtin-front');
  const rear = nonFront.find((c) => c.kind === 'builtin-rear');
  if (rear) return rear.device;
  if (nonFront.length === 1) return nonFront[0].device;
  return null;
}

/**
 * USB microscope / UVC camera — never the built-in front selfie cam.
 * @param {MediaDeviceInfo[]} cams
 * @returns {MediaDeviceInfo|null}
 */
export function pickUsbMicroscopeCamera(cams) {
  const list = Array.isArray(cams) ? cams.filter((d) => d && d.deviceId) : [];
  if (!list.length) return null;

  const microNamed = list.find((d) => MICRO_UVC_RE.test(d.label || ''));
  if (microNamed) return microNamed;

  const stored = readStoredMacroUsbDeviceId();
  if (stored) {
    const hit = list.find((d) => d.deviceId === stored);
    if (hit) return hit;
  }

  const marked = list.find((d) => isUsbMicroscopeCandidate(d, list));
  if (marked) return marked;

  const described = describeVideoInputs(list);
  const cam2 = described
    .map((c) => ({ c, n: parseCamera2Index(c.device?.label || '') }))
    .filter((x) => x.n != null)
    .sort((a, b) => a.n - b.n);
  // Dual ExtCam on GM76S: lowest camera2 / facing-back is usually USB MIC.
  if (cam2.length >= 2) {
    const backLabeled = cam2.find((x) => REAR_RE.test(x.c.device?.label || ''));
    return (backLabeled || cam2[0]).c.device;
  }

  const external = described.find((c) => c.kind === 'external');
  if (external) return external.device;

  const nonFront = described.filter((c) => c.kind !== 'builtin-front' && !isBuiltinDeviceCamera(c.device));
  if (nonFront.length === 1) return nonFront[0].device;
  if (nonFront.length > 1) {
    const rear = nonFront.find((c) => c.kind === 'builtin-rear');
    if (described.length >= 3) return nonFront[nonFront.length - 1].device;
    if (rear) return rear.device;
    return nonFront[nonFront.length - 1].device;
  }

  return described[described.length - 1].device;
}

/** Older System WebViews on custom tablets (e.g. GM76S Chrome/120). */
export function isLegacyCustomAndroidWebView() {
  if (typeof navigator === 'undefined') return false;
  const ua = String(navigator.userAgent || '');
  if (!/Android/i.test(ua)) return false;
  const m = ua.match(/Chrome\/(\d+)/i);
  return Boolean(m && Number(m[1]) > 0 && Number(m[1]) < 130);
}

/** GM76S salon kiosk / Nyafaz Android WebView — macro should use native UVC, not videoinput. */
export function isSalonApkMacroKiosk() {
  if (typeof window === 'undefined') return false;
  if (isNyufazSalonKioskViewport()) return true;
  const ua = String(navigator?.userAgent || '');
  if (/Android/i.test(ua) && /Nyafaz/i.test(ua)) return true;
  return isLegacyCustomAndroidWebView();
}

/** Android WebView / Nyafaz APK — getUserMedia must run from a user tap. */
export function requiresCameraUserGesture() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  if (window.__nyafazBridgeReady) return true;
  const ua = String(navigator.userAgent || '');
  if (/Android/i.test(ua) && (/Nyafaz/i.test(ua) || /\bwv\b/i.test(ua))) return true;
  return isLegacyCustomAndroidWebView();
}

export function readStoredMacroUsbDeviceId() {
  try {
    return String(localStorage.getItem(MACRO_USB_DEVICE_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function storeMacroUsbDeviceId(deviceId) {
  const id = typeof deviceId === 'string' ? deviceId.trim() : '';
  if (!id) return;
  try {
    localStorage.setItem(MACRO_USB_DEVICE_KEY, id);
  } catch {
    /* ignore */
  }
}

export function readStoredMacroNativeUsbId() {
  try {
    return String(localStorage.getItem(MACRO_NATIVE_USB_ID_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function storeMacroNativeUsbId(nativeId) {
  const id = nativeId == null ? '' : String(nativeId).trim();
  if (!id) return;
  try {
    localStorage.setItem(MACRO_NATIVE_USB_ID_KEY, id);
  } catch {
    /* ignore */
  }
}

export function readStoredSalonFaceCameraDeviceId() {
  try {
    return String(localStorage.getItem(SALON_FACE_CAMERA_DEVICE_KEY) || '').trim();
  } catch {
    return '';
  }
}

export function storeSalonFaceCameraDeviceId(deviceId) {
  try {
    const id = typeof deviceId === 'string' ? deviceId.trim() : '';
    if (!id) {
      localStorage.removeItem(SALON_FACE_CAMERA_DEVICE_KEY);
      return;
    }
    localStorage.setItem(SALON_FACE_CAMERA_DEVICE_KEY, id);
  } catch {
    /* ignore */
  }
}

function timeoutReject(ms, message) {
  return new Promise((_, reject) => {
    window.setTimeout(() => reject(new Error(message)), ms);
  });
}

/**
 * Soft permission prime using the built-in front camera only.
 * Never use `{ video: true }` — on custom Android that often grabs the USB cam
 * and leaves the UVC driver unavailable for macro capture.
 * @param {number} [timeoutMs]
 * @returns {Promise<MediaStream|null>}
 */
export async function primeVideoPermission(timeoutMs = 4000) {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return null;
  }
  try {
    return await Promise.race([
      navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'user' },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      }),
      timeoutReject(timeoutMs, 'Camera permission timed out'),
    ]);
  } catch {
    return null;
  }
}

/**
 * True when the browser already exposes camera deviceIds (permission previously granted).
 */
export async function hasCameraPermissionGranted() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return false;
  }
  try {
    if (navigator.permissions?.query) {
      try {
        const st = await navigator.permissions.query({ name: /** @type {PermissionName} */ ('camera') });
        if (st?.state === 'granted') return true;
        if (st?.state === 'denied') return false;
      } catch {
        /* camera permission name unsupported on many WebViews */
      }
    }
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.some((d) => d?.kind === 'videoinput' && d.deviceId);
  } catch {
    return false;
  }
}

/**
 * Ask the system/WebView for camera access. MUST run from a user tap on custom
 * Android OEMs — auto getUserMedia on page load often never shows "Allow".
 * Briefly opens then stops a stream so labels/deviceIds appear.
 * @param {{ timeoutMs?: number }} [opts]
 * @returns {Promise<boolean>}
 */
export async function requestCameraAccessWithPrompt(opts = {}) {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    return false;
  }
  const timeoutMs = opts.timeoutMs ?? 20000;

  // Prefer native host permission first (Salon APK) when available.
  try {
    const native = typeof window !== 'undefined' ? window.NyafazNative : null;
    if (typeof native?.requestCameraPermission === 'function') {
      await Promise.resolve(native.requestCameraPermission());
    } else if (typeof native?.requestPermissions === 'function') {
      await Promise.resolve(native.requestPermissions(['camera']));
    }
  } catch {
    /* continue with getUserMedia */
  }

  let stream = null;
  try {
    // Never use `{ video: true }` on custom boards — ExtCam often binds USB MIC first
    // and steals it from native UVC / wrong face preview.
    stream = await Promise.race([
      navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'user' },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
        audio: false,
      }),
      timeoutReject(timeoutMs, 'Camera permission timed out'),
    ]);
    return true;
  } catch {
    try {
      const cams = await listMicroscopeVideoInputs();
      const pick = pickBuiltinFrontCamera(cams);
      if (pick?.deviceId) {
        stream = await Promise.race([
          navigator.mediaDevices.getUserMedia({
            video: { deviceId: { ideal: pick.deviceId } },
            audio: false,
          }),
          timeoutReject(timeoutMs, 'Camera permission timed out'),
        ]);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  } finally {
    stream?.getTracks?.().forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
  }
}

function videoInputsWithIds(all) {
  return (Array.isArray(all) ? all : []).filter(
    (d) => d && d.kind === 'videoinput' && typeof d.deviceId === 'string' && d.deviceId.trim(),
  );
}

/**
 * @returns {Promise<MediaDeviceInfo[]>}
 */
export async function listMicroscopeVideoInputs() {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.enumerateDevices) {
    return [];
  }
  let primed = null;
  try {
    // Prefer enumerate without opening a stream first (faster; avoids USB exclusive lock).
    let cams = videoInputsWithIds(await navigator.mediaDevices.enumerateDevices());
    if (cams.length) return cams;

    // No deviceIds until permission — prime front cam only, then re-list.
    primed = await primeVideoPermission(4000);
    cams = videoInputsWithIds(await navigator.mediaDevices.enumerateDevices());
    if (cams.length) return cams;

    // Still empty: return raw videoinputs (may lack ids on broken OEMs).
    const all = await navigator.mediaDevices.enumerateDevices();
    return all.filter((d) => d && d.kind === 'videoinput');
  } finally {
    primed?.getTracks?.().forEach((t) => {
      try {
        t.stop();
      } catch {
        /* ignore */
      }
    });
  }
}

/**
 * Open USB / selected microscope camera. Does not silently fall back to
 * `{ video: true }` when a deviceId was requested (that grabs the wrong cam).
 * @param {{ deviceId?: string, timeoutMs?: number }} [opts]
 */
export async function openMicroscopeVideoStream(opts = {}) {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('Camera API unavailable');
  }
  const timeoutMs = opts.timeoutMs ?? 15000;
  const id = typeof opts.deviceId === 'string' ? opts.deviceId.trim() : '';

  const attempts = [];
  if (id) {
    attempts.push({ video: { deviceId: { exact: id } }, audio: false });
    attempts.push({ video: { deviceId: { ideal: id } }, audio: false });
  } else {
    // Prefer environment/rear-ish before unconstrained default (often front).
    attempts.push({ video: { facingMode: { ideal: 'environment' } }, audio: false });
    attempts.push({ video: true, audio: false });
  }

  let lastErr = null;
  for (const constraints of attempts) {
    try {
      return await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        timeoutReject(timeoutMs, 'Microscope camera timed out'),
      ]);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error('Microscope camera unavailable');
}

/**
 * Try preferred id, then each non-front camera until one opens.
 * @param {MediaDeviceInfo[]} cams
 * @param {string} [preferredId]
 * @param {{ timeoutMs?: number }} [opts]
 */
export async function openBestMicroscopeStream(cams, preferredId = '', opts = {}) {
  const timeoutMs = opts.timeoutMs ?? 10000;
  const described = describeVideoInputs(cams);
  const ordered = [];
  const prefer = String(preferredId || '').trim();
  if (prefer) {
    const hit = described.find((c) => c.device.deviceId === prefer);
    if (hit) ordered.push(hit.device);
  }
  const pick = pickUsbMicroscopeCamera(cams);
  if (pick && !ordered.some((d) => d.deviceId === pick.deviceId)) ordered.push(pick);
  described
    .filter((c) => c.kind !== 'builtin-front')
    .forEach((c) => {
      if (!ordered.some((d) => d.deviceId === c.device.deviceId)) ordered.push(c.device);
    });
  if (!ordered.length && described.length) ordered.push(described[described.length - 1].device);

  let lastErr = null;
  for (const device of ordered) {
    try {
      const stream = await openMicroscopeVideoStream({
        deviceId: device.deviceId,
        timeoutMs,
      });
      storeMacroUsbDeviceId(device.deviceId);
      return { stream, deviceId: device.deviceId };
    } catch (e) {
      lastErr = e;
    }
  }

  try {
    const stream = await openMicroscopeVideoStream({ deviceId: '', timeoutMs });
    return { stream, deviceId: '' };
  } catch (e) {
    throw lastErr || e;
  }
}

/**
 * Open built-in / kiosk face camera only. Prefers explicit deviceId from
 * [pickBuiltinFrontCamera]. Never opens USB MIC / macro ExtCam, even as fallback.
 * @param {{ deviceId?: string, timeoutMs?: number, width?: number, height?: number, facing?: 'user'|'environment' }} [opts]
 */
export async function openBuiltinFaceCameraStream(opts = {}) {
  if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    throw new Error('Camera API unavailable');
  }
  const timeoutMs = opts.timeoutMs ?? 15000;
  const width = opts.width ?? 1280;
  const height = opts.height ?? 720;
  const facing = opts.facing === 'environment' ? 'environment' : 'user';
  const pickBuiltin = facing === 'environment' ? pickBuiltinRearCamera : pickBuiltinFrontCamera;
  const facingIdeal = facing === 'environment' ? 'environment' : 'user';
  let id = typeof opts.deviceId === 'string' ? opts.deviceId.trim() : '';
  const legacy = isLegacyCustomAndroidWebView();

  let cams = [];
  try {
    cams = await listMicroscopeVideoInputs();
  } catch {
    cams = [];
  }
  if (!id) {
    id = pickBuiltin(cams)?.deviceId || '';
  } else if (cams.length && isUsbMicroscopeCandidate(cams.find((d) => d.deviceId === id) || { deviceId: id, label: '' }, cams)) {
    // Caller passed macro UVC by mistake — force face pick.
    id = pickBuiltin(cams)?.deviceId || '';
  }

  const faceCams = cams.filter((d) => d?.deviceId && !isUsbMicroscopeCandidate(d, cams));
  const orderedIds = [];
  const pushId = (deviceId) => {
    const v = typeof deviceId === 'string' ? deviceId.trim() : '';
    if (v && !orderedIds.includes(v)) orderedIds.push(v);
  };
  pushId(id);

  // Prefer SoC/internal device cams, then higher camera2 (HD), never microscope ids.
  faceCams
    .filter((d) => isBuiltinDeviceCamera(d))
    .forEach((d) => pushId(d.deviceId));
  const cam2 = faceCams
    .map((d) => ({ id: d.deviceId, n: parseCamera2Index(d.label || ''), label: d.label || '' }))
    .filter((x) => x.id && x.n != null)
    .sort((a, b) => {
      if (facing === 'environment') {
        const aRear = REAR_RE.test(a.label) ? 1 : 0;
        const bRear = REAR_RE.test(b.label) ? 1 : 0;
        if (bRear !== aRear) return bRear - aRear;
        return b.n - a.n;
      }
      const aFront = FRONT_RE.test(a.label) ? 1 : 0;
      const bFront = FRONT_RE.test(b.label) ? 1 : 0;
      if (bFront !== aFront) return bFront - aFront;
      return b.n - a.n;
    });
  cam2.forEach((x) => pushId(x.id));
  faceCams.forEach((d) => pushId(d.deviceId));

  const size = {
    width: { ideal: width },
    height: { ideal: height },
    frameRate: { ideal: 30, max: 30 },
  };

  const attempts = [];
  for (const deviceId of orderedIds) {
    attempts.push({ video: { deviceId: { exact: deviceId }, ...size }, audio: false });
    attempts.push({ video: { deviceId: { ideal: deviceId }, ...size }, audio: false });
    // Soft size fallback — some ExtCam HD nodes reject 1280x720.
    attempts.push({ video: { deviceId: { exact: deviceId } }, audio: false });
    attempts.push({ video: { deviceId: { ideal: deviceId } }, audio: false });
  }
  // Avoid unconstrained / video:true on legacy Rockchip — it binds USB MIC.
  if (!legacy) {
    attempts.push({ video: { facingMode: { ideal: facingIdeal }, ...size }, audio: false });
    attempts.push({ video: { facingMode: { ideal: facingIdeal } }, audio: false });
  } else if (orderedIds.length === 0) {
    attempts.push({ video: { facingMode: { ideal: facingIdeal }, ...size }, audio: false });
  }

  let lastErr = null;
  for (const constraints of attempts) {
    try {
      return await Promise.race([
        navigator.mediaDevices.getUserMedia(constraints),
        timeoutReject(timeoutMs, 'Main camera timed out'),
      ]);
    } catch (e) {
      lastErr = e;
      await new Promise((r) => window.setTimeout(r, 350));
    }
  }
  throw lastErr || new Error('Main camera unavailable');
}

/**
 * @param {() => HTMLElement|null|undefined} getEl
 * @param {{ timeoutMs?: number, minSize?: number }} [opts]
 */
export async function waitForPreviewLayout(getEl, opts = {}) {
  const timeoutMs = opts.timeoutMs ?? 4000;
  const minSize = opts.minSize ?? 8;
  const start = Date.now();
  return new Promise((resolve) => {
    const tick = () => {
      const el = typeof getEl === 'function' ? getEl() : null;
      if (el && typeof el.getBoundingClientRect === 'function') {
        const r = el.getBoundingClientRect();
        if (r.width > minSize && r.height > minSize) {
          resolve({
            left: Math.round(r.left),
            top: Math.round(r.top),
            width: Math.round(r.width),
            height: Math.round(r.height),
          });
          return;
        }
      }
      if (Date.now() - start >= timeoutMs) {
        resolve(null);
        return;
      }
      window.requestAnimationFrame(tick);
    };
    tick();
  });
}
